import React, { useCallback, useEffect, useState } from 'react';
import { healthsherpa } from './api';
import { usePersonColors } from './HealthSherpaPerson';
import { fs } from './hsScale';
import { ConsentChip, ConsentSendDialog } from './HealthSherpaConsent';   // 5.40.0
import { transfer } from './hsLock';   // 5.44.0 every way into HealthSherpa takes the "in use" lock

// =============================================================================
// HealthSherpa "Open Enrollment" worklist (5.39.0, Henry 2026-11-01). Same visibility as the Clients tab (admins, or
// staff when "Show HealthSherpa Clients to staff" is on). One row per household with an active / pending application
// in the previous plan year; whether the coming year's application has already appeared (daily refresh); our own
// worklist status + append-only notes (every change audited on the server). Our database only — nothing here calls
// HealthSherpa. Filters live in component state and go to the server in a POST body (5.27.4 rule).
// Privacy: the same switch as the Clients tab (insuredocs.hs.privacy.v1) blurs names and phones (.hs-pii, no titles).
// =============================================================================

const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
const api = () => healthsherpa.oe;
const PRIVACY_KEY = 'insuredocs.hs.privacy.v1';
const money = (cents) => (cents === null || cents === undefined ? '—' : `$${(cents / 100).toFixed(2)}`);
const nameOf = (r) => [r.first_name, r.last_name].filter(Boolean).join(' ') || r.display_name || `Household #${r.person_id}`;
const STATUS_TONE = { not_started: '#6b7280', contacted: '#1f5d99', waiting: '#b9770e', done: '#2e7d32', not_renewing: '#7f1d1d' };

const css = `
.hs-oe table { border-collapse: collapse; width: 100%; }
.hs-oe th { text-align: left; font-size: calc(12px * var(--hs-scale, 1)); font-weight: 600; padding: 6px 8px; border-bottom: 2px solid var(--oe-border); color: var(--oe-sub); white-space: nowrap; }
.hs-oe td { text-align: left; font-size: calc(13px * var(--hs-scale, 1)); padding: 6px 8px; border-bottom: 1px solid var(--oe-border); vertical-align: top; }
.hs-oe .hs-pii { filter: blur(5px); user-select: none; }
.hs-oe .oe-sub { color: var(--oe-sub); font-size: calc(12px * var(--hs-scale, 1)); }
`;

function usePrivacy() {
  const [on, setOn] = useState(() => { try { return window.localStorage.getItem(PRIVACY_KEY) === '1'; } catch (_e) { return false; } });
  const toggle = () => {
    const v = !on;
    setOn(v);
    try { window.localStorage.setItem(PRIVACY_KEY, v ? '1' : '0'); } catch (_e) { /* storage unavailable */ }
  };
  return [on, toggle];
}

const btn = (c, primary, disabled) => ({
  padding: '5px 10px', fontSize: fs(12), borderRadius: 5, cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap',
  fontWeight: primary ? 600 : 400, opacity: disabled ? 0.55 : 1, textDecoration: 'none', display: 'inline-block',
  border: primary ? '1px solid #185FA5' : `1px solid ${c.border}`, background: primary ? '#185FA5' : c.panel, color: primary ? '#fff' : c.text,
});
const sel = (c) => ({ padding: '5px 8px', fontSize: fs(12), borderRadius: 5, border: `1px solid ${c.border}`, background: c.panel, color: c.text });

function Notes({ c, row, season, onChanged, privacy }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api().notes(row.person_id, season).then((r) => { if (alive) setNotes(r.data.notes); }).catch((e) => { if (alive) setErr(detailOf(e)); });
    return () => { alive = false; };
  }, [row.person_id, season]);
  const add = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api().addNote(row.person_id, text, season);
      setNotes((n) => [r.data, ...(n || [])]); setText(''); onChanged();
    } catch (e) { setErr(detailOf(e)); }
    setBusy(false);
  };
  return (
    <div style={{ padding: '8px 4px', display: 'grid', gap: 8 }} data-testid="oe-notes">
      <div style={{ display: 'flex', gap: 8 }}>
        <textarea aria-label="New note" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={2}
          style={{ flex: 1, padding: 6, fontSize: fs(13), borderRadius: 5, border: `1px solid ${c.border}`, background: c.panel, color: c.text }} />
        <button style={btn(c, true, busy || !text.trim())} disabled={busy || !text.trim()} onClick={add}>Add note</button>
      </div>
      {err && <div role="alert" style={{ color: '#c0392b', fontSize: fs(12) }}>{err}</div>}
      {notes && notes.length === 0 && <div className="oe-sub">No notes yet.</div>}
      {(notes || []).map((n) => (
        <div key={n.id} style={{ fontSize: fs(13) }}>
          <span className="oe-sub">{n.at_label} · {n.by || 'someone'} — </span>
          <span className={privacy ? 'hs-pii' : undefined}>{n.body}</span>
        </div>
      ))}
    </div>
  );
}

export default function OpenEnrollmentTab({ darkMode, onStartEnrollment }) {
  const c = usePersonColors(darkMode);
  const [filters, setFilters] = useState({ status: '', found: '', owner: '', carrier: '', aor: '' });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [reload, setReload] = useState(0);
  const [open, setOpen] = useState(null);
  const [privacy, togglePrivacy] = usePrivacy();
  const [consentFor, setConsentFor] = useState(null);       // 5.40.0: the row whose consent is being sent
  const refresh = useCallback(() => setReload((x) => x + 1), []);

  useEffect(() => {
    let alive = true;
    api().list(filters).then((r) => { if (alive) { setData(r.data); setErr(null); } })
      .catch((e) => { if (alive) setErr(e?.response?.status === 403 ? "The Open Enrollment list isn't open to staff yet." : detailOf(e)); });
    return () => { alive = false; };
  }, [filters, reload]);

  const setStatus = async (r, status) => {
    try { await api().setStatus(r.person_id, status, data.season); refresh(); } catch (e) { setErr(detailOf(e)); }
  };
  const f = (k) => (e) => setFilters((x) => ({ ...x, [k]: e.target.value }));
  const vars = { '--oe-border': c.border, '--oe-sub': c.sub };
  const pii = privacy ? 'hs-pii' : undefined;
  const counts = data ? data.counts : null;
  const label = (code) => ((data && data.statuses.find((s) => s.code === code)) || {}).label || code;

  return (
    <div className="hs-oe" style={{ padding: 18, display: 'grid', gap: 12, ...vars }} data-testid="oe-tab">
      <style>{css}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: fs(16), fontWeight: 700 }}>Open Enrollment {data ? data.season : ''}</div>
        {counts && (
          <span style={{ fontSize: fs(13), color: c.sub }} data-testid="oe-counts">
            {counts.total} active / pending in {data.source_year} · {counts.next_found} with a {data.season} application
            {' · '}{data.statuses.map((s) => `${s.label} ${counts.by_status[s.code] || 0}`).join(' · ')}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button onClick={togglePrivacy} aria-pressed={privacy} data-testid="oe-privacy" style={btn(c, privacy)}
          title={privacy ? 'Privacy is on: names and phones are blurred — click to show them' : 'Blur names and phones'}>👁 {privacy ? 'Privacy on' : 'Privacy'}</button>
      </div>
      {data && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }} data-testid="oe-filters">
          <select aria-label="Status" style={sel(c)} value={filters.status} onChange={f('status')}>
            <option value="">Every status</option>
            {data.statuses.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
          <select aria-label={`${data.season} application found`} style={sel(c)} value={filters.found} onChange={f('found')}>
            <option value="">{data.season} application: any</option><option value="yes">{data.season} found</option><option value="no">{data.season} not found</option>
          </select>
          <select aria-label="Owner" style={sel(c)} value={filters.owner} onChange={f('owner')}>
            <option value="">Any owner</option><option value="me">Mine</option><option value="none">Unclaimed</option>
            {(data.users || []).map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
          </select>
          <select aria-label="Carrier" style={sel(c)} value={filters.carrier} onChange={f('carrier')}>
            <option value="">Any carrier</option>
            {data.carriers.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select aria-label="Agent of record" style={sel(c)} value={filters.aor} onChange={f('aor')}>
            <option value="">AOR: any</option><option value="ours">AOR: ours</option><option value="other">AOR: another agent</option>
          </select>
          <span style={{ fontSize: fs(12), color: c.sub }}>{data.shown} shown</span>
        </div>
      )}
      {err && <div role="alert" style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: fs(13) }}>{err}</div>}
      {data && (
        <div style={{ background: c.panel, border: `1px solid ${c.border}`, borderRadius: 8, overflowX: 'auto' }}>
          <table data-testid="oe-table">
            <thead>
              <tr>
                <th>Client</th><th>Phone</th><th>{data.source_year} carrier / plan</th><th>Net</th><th>AOR</th><th>Owner</th>
                <th>{data.season} application</th><th>Consent</th><th>Status</th><th>Last note</th><th>Updated</th><th />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <React.Fragment key={r.person_id}>
                  <tr data-testid="oe-row" data-pid={r.person_id}>
                    <td className={pii} title={privacy ? undefined : nameOf(r)}><strong>{nameOf(r)}</strong>
                      {r.client && <div className="oe-sub">{r.client.full_name}</div>}</td>
                    <td className={pii}>{r.phone || <span className="oe-sub">—</span>}</td>
                    <td>{r.carrier || '—'}<div className="oe-sub">{r.plan || ''}{r.status === 'pending' ? ' · pending' : ''}</div></td>
                    <td>{money(r.net_premium_cents)}</td>
                    <td>{r.aor ? (r.aor.ours === true ? <span style={{ color: '#2e7d32', fontWeight: 600 }}>Ours</span>
                      : r.aor.ours === false ? <span style={{ color: '#b9770e', fontWeight: 600 }} title={privacy ? undefined : r.aor.label}>Other</span>
                        : <span className="oe-sub">?</span>) : <span className="oe-sub">—</span>}</td>
                    <td>{r.owner ? r.owner.name : <span className="oe-sub">unclaimed</span>}</td>
                    <td>{r.next_found ? <span style={{ color: '#2e7d32', fontWeight: 600 }}>✓ {r.next_status || 'found'}</span> : <span className="oe-sub">not yet</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <ConsentChip consent={r.consent} testid="oe-consent" />{' '}
                      <button style={{ ...btn(c, false), padding: '2px 8px' }} onClick={() => setConsentFor(r)} data-testid="oe-consent-send">
                        {r.consent && r.consent.state !== 'none' ? 'Resend' : 'Send'}
                      </button>
                    </td>
                    <td>
                      <select aria-label="Worklist status" value={r.work_status} onChange={(e) => setStatus(r, e.target.value)}
                        style={{ ...sel(c), color: STATUS_TONE[r.work_status] || c.text, fontWeight: 600 }} data-testid="oe-status">
                        {data.statuses.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                      </select>
                    </td>
                    <td style={{ maxWidth: 260 }}>{r.last_note ? <><span className={pii}>{r.last_note.body}</span><div className="oe-sub">{r.last_note.by}</div></> : <span className="oe-sub">—</span>}</td>
                    <td>{r.updated ? <><div>{r.updated.by || ''}</div><div className="oe-sub">{r.updated.at_label}</div></> : <span className="oe-sub">—</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button style={btn(c, false)} onClick={() => setOpen(open === r.person_id ? null : r.person_id)} aria-expanded={open === r.person_id}>Notes</button>{' '}
                      {r.hs_link && <button type="button" onClick={() => transfer(r.hs_link)} style={btn(c, false)} data-testid="hs-open-oe">Open in HealthSherpa ↗</button>}{' '}
                      {onStartEnrollment && <button style={btn(c, true)} onClick={() => onStartEnrollment({ kind: 'person', id: r.person_id })} data-testid="oe-start">Start enrollment</button>}
                    </td>
                  </tr>
                  {open === r.person_id && (
                    <tr><td colSpan={12}><Notes c={c} row={r} season={data.season} onChanged={refresh} privacy={privacy} /></td></tr>
                  )}
                </React.Fragment>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={12} className="oe-sub">Nobody matches these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {!data && !err && <div style={{ fontSize: fs(13), color: c.sub }}>Loading…</div>}
      {consentFor && (
        <ConsentSendDialog darkMode={darkMode} target={{ person_id: consentFor.person_id }}
          initial={{ consumer_name: nameOf(consentFor), phone: consentFor.phone || '' }}
          onClose={() => { setConsentFor(null); refresh(); }} />
      )}
      {data && <div className="oe-sub">Status “{label('not_started')}” = nobody has worked this household yet. Every change is logged.</div>}
    </div>
  );
}
